import React, { useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import useTranslation from "../../../shared/hooks/useTranslation";
import { dt } from "../utils/dt";
import { Machine } from "../models/models";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import customerMachineryApi from "../api/customerMachineryApi";
import {
  FormTextarea,
  FormSelect,
  FormFieldLabel,
  FormActionBar,
  FormSelectOption,
} from "../../../shared/components/redesign/forms";

interface ReportProblemDialogProps {
  open: boolean;
  machine: Machine;
  onClose: () => void;
  onSuccess?: (srNumber: string) => void;
}

const TERRACOTTA = "#D4572B";

const ReportProblemDialog: React.FC<ReportProblemDialogProps> = ({
  open,
  machine,
  onClose,
  onSuccess,
}) => {
  const { translate } = useTranslation("myMachinery");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<string>("Normal");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successNumber, setSuccessNumber] = useState<string | null>(null);

  const displayName = getMachineDisplayName(machine);

  const priorityOptions: FormSelectOption[] = [
    { value: "Low", label: translate("reportProblem.priorityLow") },
    { value: "Normal", label: translate("reportProblem.priorityNormal") },
    { value: "High", label: translate("reportProblem.priorityHigh") },
    { value: "Critical", label: translate("reportProblem.priorityCritical") },
  ];

  const handleReset = () => {
    setDescription("");
    setPriority("Normal");
    setSubmitting(false);
    setError(null);
    setSuccessNumber(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await customerMachineryApi.reportProblem(machine.id, {
        description: description.trim(),
        priorityCode: priority,
      });
      setSuccessNumber(response.serviceReportNumber);
      onSuccess?.(response.serviceReportNumber);
    } catch (err: any) {
      setError(translate("reportProblem.error"));
    } finally {
      setSubmitting(false);
    }
  };

  // ===== SUCCESS STATE =====
  if (successNumber) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        PaperProps={{
          sx: {
            borderRadius: isPhone ? 0 : dt.radius,
            fontFamily: dt.font,
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 5,
            px: 3,
            textAlign: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              backgroundColor: "rgba(39, 174, 96, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) both",
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 32, color: "#27AE60" }} />
          </Box>

          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "18px",
              fontWeight: 700,
              color: dt.textPrimary,
              animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.05s both",
            }}
          >
            {translate("reportProblem.success")}
          </Box>

          <Box
            sx={{
              fontFamily: dt.fontMono,
              fontSize: "15px",
              fontWeight: 600,
              color: TERRACOTTA,
              backgroundColor: `${TERRACOTTA}0A`,
              border: `1px solid ${TERRACOTTA}30`,
              borderRadius: dt.radiusSm,
              px: 2,
              py: 0.75,
              letterSpacing: "0.5px",
              animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both",
            }}
          >
            {successNumber}
          </Box>

          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textSecondary,
              maxWidth: 320,
              lineHeight: 1.5,
              animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.15s both",
            }}
          >
            {translate("reportProblem.successDetail", { number: successNumber })}
          </Box>

          <Box
            onClick={handleClose}
            sx={{
              mt: 1,
              fontFamily: dt.font,
              fontSize: "14px",
              fontWeight: 600,
              color: dt.blue,
              cursor: "pointer",
              transition: "opacity 0.2s ease",
              animation: "fadeUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both",
              "&:hover": { opacity: 0.8 },
            }}
          >
            {translate("reportProblem.close")}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // ===== FORM STATE =====
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : dt.radius,
          fontFamily: dt.font,
          overflow: "hidden",
        },
      }}
    >
      {/* Title bar with terracotta left accent */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: dt.font,
          fontSize: "16px",
          fontWeight: 700,
          color: dt.textPrimary,
          borderBottom: `1px solid ${dt.cardBorder}`,
          borderLeft: `4px solid ${TERRACOTTA}`,
          py: 2,
          px: 2.5,
        }}
      >
        {translate("reportProblem.title")}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: dt.textMuted }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
        {/* Machine context header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
            p: 1.5,
            backgroundColor: dt.inputBg,
            borderRadius: dt.radiusSm,
            border: `1px solid ${dt.cardBorder}`,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.cardBorder}`,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#F8FAFC",
              flexShrink: 0,
            }}
          >
            {(machine.image?.content ||
              machine.catalogModel?.imageStandardUrl) ? (
              <img
                src={
                  machine.image?.content ||
                  machine.catalogModel?.imageStandardUrl || ""
                }
                alt={displayName}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box
                sx={{
                  fontSize: "16px",
                  color: dt.textMuted,
                  fontWeight: 700,
                  fontFamily: dt.fontMono,
                }}
              >
                ?
              </Box>
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 600,
                color: dt.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Box>
            {machine.serialNumber && (
              <Box
                sx={{
                  fontFamily: dt.fontMono,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: dt.textSecondary,
                  letterSpacing: "0.3px",
                }}
              >
                S/N: {machine.serialNumber}
              </Box>
            )}
          </Box>
        </Box>

        {/* Description field */}
        <Box sx={{ mb: 2 }}>
          <FormFieldLabel required>
            {translate("reportProblem.description")}
          </FormFieldLabel>
          <FormTextarea
            value={description}
            onChange={setDescription}
            placeholder={translate("reportProblem.descriptionPlaceholder")}
            rows={4}
            minHeight="100px"
            maxLength={2000}
          />
        </Box>

        {/* Priority field */}
        <Box sx={{ mb: 1 }}>
          <FormFieldLabel>
            {translate("reportProblem.priority")}
          </FormFieldLabel>
          <FormSelect
            value={priority}
            onChange={(val) => setPriority(String(val))}
            options={priorityOptions}
            size="small"
          />
        </Box>

        {/* Error message */}
        {error && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              fontSize: "13px",
              fontFamily: dt.font,
              color: "#C0392B",
              backgroundColor: "rgba(192, 57, 43, 0.06)",
              border: "1px solid rgba(192, 57, 43, 0.2)",
              borderRadius: dt.radiusSm,
            }}
          >
            {error}
          </Box>
        )}

        {/* Action bar */}
        <FormActionBar
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitLabel={translate("reportProblem.submit")}
          cancelLabel={translate("reportProblem.cancel")}
          submitDisabled={!description.trim()}
          loading={submitting}
          animation={false}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ReportProblemDialog;
